import {useDevAlert} from '@packages/systems/spring-hooks';
import {emptyObject} from '@packages/utilities/fp/utils';

import {truncateContent} from '../../utils/truncate';

import React from 'react';

/**
 * line-clamp is a non-standard CSS feature.
 * The `style` prop in React does not pass it through correctly (either as `line-clamp` or `--webkit-line-clamp`).
 *
 * We need consumers to rig up the variable to their `emotionStyled` styles like so (note the @ts-expect-error issue):
 *
 * ```
 * const TruncatedComponent = emotionStyled('div')({
 * // @ts-expect-error - Property.LineClamp is incorrectly typed. This is a `var()` string, which is syntactically correct.
 *  lineClamp: lineClampVar
 * })
 */
export const lineClampVar = {
  get: 'var(--component-lineClamp)',
  set: '--component-lineClamp',
};

/**
 * Shared props for Truncation features.
 *
 * We are currently _not_ adding `truncationTooltipProps` to this, since that is a much larger task.
 *
 * @TODO Determine how dynamic truncation `Tooltip` will interact with `useTruncation` hook
 * @see https://webflow.atlassian.net/browse/DS-1835
 */
export type TruncationProps = {
  /**
   * The number of lines to display before truncating with a trailing ellipsis, can be used for single- and multi-line truncation.
   */
  truncateToLines?: number;
  /**
   * The maximum number of characters to display before truncating. Used for custom truncation.
   */
  truncationLimit?: number;
  /**
   * The type of truncation to apply to the text.  Used for custom truncation.
   */
  truncationPosition?: 'start' | 'middle' | 'end';
  /**
   * Whether the consumer intendeds to display a tooltip on truncated text.
   *
   * We use this to determine whether we should add the resize observer for CSS truncation.
   * Doing so will be a small performance gain for consumers who don't need it.
   */
  shouldNotShowTooltip?: boolean;
};

type UseTruncationProps<TChildren> = TruncationProps & {
  children: TChildren;
};

/**
 * A hook that handles truncating text.
 * Given a component and truncation props, it can either:
 *   - Truncate the text in certain positions using Javascript
 *   - Truncate the text to a certain number of lines using CSS
 *
 * It returns the `children` and a style object.
 *
 * Spread the style object on a wrapper around the `children` to add truncation styling.
 *
 * ```jsx
 *
 * const SomeComponent = ({truncationProps, children}) => {
 *    const { content, truncationStyles } = useTruncation(truncationProps, children);
 *
 *    return (
 *      <div style={truncationStyles}>
 *        {content}
 *      </div>
 *    );
 * };
 * }
 *
 * ```
 */
export const useTruncation = <TChildren,>({
  truncateToLines,
  truncationLimit,
  truncationPosition = 'end',
  shouldNotShowTooltip,
  children,
}: UseTruncationProps<TChildren>) => {
  const textRef = React.useRef<HTMLElement | undefined>(undefined);
  const [isCSSTruncated, setIsCSSTruncated] = React.useState<boolean>(false);

  const hasCustomTruncation = truncationLimit && truncationPosition;

  useDevAlert(
    Boolean(hasCustomTruncation) && Boolean(typeof children !== 'string'),
    'Truncated content must be a string when using truncationLimit and truncationPosition.'
  );

  const truncationStyles = React.useMemo(() => {
    if (!truncateToLines) {
      return emptyObject;
    }
    if (truncateToLines === 1) {
      return {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      } as const;
    } else {
      return {
        display: '-webkit-box',
        overflow: 'hidden',
        WebkitBoxOrient: 'vertical',
        [lineClampVar.set]: truncateToLines,
      } as const;
    }
  }, [truncateToLines]);

  const {content, didCustomTruncate} = React.useMemo(() => {
    if (typeof children === 'string' && hasCustomTruncation) {
      return truncateContent(children, {
        truncationPosition,
        limit: truncationLimit,
        numberOfLines: 1,
      });
    } else {
      return {content: children, didCustomTruncate: false};
    }
  }, [children, hasCustomTruncation, truncationPosition, truncationLimit]);

  React.useEffect(() => {
    if (
      typeof truncateToLines !== 'number' ||
      truncateToLines < 1 ||
      shouldNotShowTooltip
    ) {
      return;
    }

    const checkTruncation = ([element]: ResizeObserverEntry[]) => {
      if (!element) {
        return false;
      }

      /**
       * We need to calculate whether the text node has been truncated with CSS.
       *
       * We do this by comparing the content `width` of the element with its `scrollWidth`.
       * If they are not equal, then it has been truncated.
       *
       * However, it's important to note that `element.contentRect` is a float, while `element.target.scroll...` is an integer.
       * We round our contentRect to make sure we get as accurate a comparison as possible.
       *
       * We _could_ check `getBoundingClientRect()` instead of `scrollWidth` & `scrollHeight`to get a float.
       * However, this check is more costly than accessing `scrollWidth` or `scrollHeight`.
       * @see https://www.measurethat.net/Benchmarks/Show/27540/0/scrollheight-vs-getboundingclientrectheight
       *
       * We may need to reassess this if we notice a significant performance regression or the rounded numbers aren't detecting our truncation accurately.
       */
      const hasBeenCSSTruncated =
        Math.round(element.contentRect.width) !== element.target.scrollWidth ||
        Math.round(element.contentRect.height) !== element.target.scrollHeight;
      setIsCSSTruncated(hasBeenCSSTruncated);
    };

    if (textRef.current) {
      const observer = new ResizeObserver(checkTruncation);
      observer.observe(textRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [truncateToLines, textRef, shouldNotShowTooltip]);

  return React.useMemo(
    () => ({
      truncationStyles,
      content,
      didCustomTruncate,
      textRef,
      isCSSTruncated,
    }),
    [truncationStyles, content, didCustomTruncate, textRef, isCSSTruncated]
  );
};
